import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 검색 필터 상태를 URL query parameter와 동기화하는 공통 hook
 * 
 * @param prefix - URL 파라미터에 사용할 prefix (예: 'pending', 'allUsers', 'blocked')
 * @param defaultFilters - 기본 필터 값들
 * @returns 필터 상태와 업데이트 함수들
 * 
 * @example
 * const {
 *   customerId,
 *   setCustomerId,
 *   status,
 *   setStatus,
 *   searchQuery,
 *   setSearchQuery,
 *   resetFilters,
 * } = useSearchFilters('pending', {
 *   customerId: null,
 *   status: 'all',
 *   searchQuery: '',
 * });
 */
export const useSearchFilters = <T extends Record<string, string | number | boolean | null | undefined>>(
  prefix: string,
  defaultFilters: T
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL에서 초기값 읽기
  const getInitialValue = useCallback(<K extends keyof T>(key: K): T[K] => {
    const paramKey = `${prefix}_${String(key)}`;
    const value = searchParams.get(paramKey);
    
    if (value === null || value === undefined) {
      return defaultFilters[key];
    }
    
    // 타입에 따라 변환
    const defaultValue = defaultFilters[key];
    if (typeof defaultValue === 'number') {
      const numValue = parseInt(value, 10);
      return (isNaN(numValue) ? defaultValue : numValue) as T[K];
    }
    if (typeof defaultValue === 'boolean') {
      return (value === 'true') as T[K];
    }
    // null과 undefined 구분 처리
    if (value === 'null') {
      return null as T[K];
    }
    if (value === 'undefined' || value === '') {
      return undefined as T[K];
    }
    if (typeof defaultValue === 'string') {
      return value as T[K];
    }
    
    return defaultValue;
  }, [prefix, searchParams, defaultFilters]);
  
  // 필터 상태 초기화
  const [filters, setFilters] = useState<T>(() => {
    const initial: Partial<T> = {};
    for (const key in defaultFilters) {
      initial[key] = getInitialValue(key);
    }
    return initial as T;
  });
  
  // URL 업데이트를 위한 pending changes 추적
  const [pendingURLUpdates, setPendingURLUpdates] = useState<Partial<T>>({});
  // URL 업데이트 중인지 추적하는 ref (동기화 방지용)
  const isUpdatingURL = useRef(false);
  
  // URL 업데이트 함수
  const updateURL = useCallback((newFilters: Partial<T>) => {
    setPendingURLUpdates(prev => ({ ...prev, ...newFilters }));
  }, []);
  
  // pending URL updates를 실제로 적용 (useEffect에서 실행)
  useEffect(() => {
    if (Object.keys(pendingURLUpdates).length === 0) return;
    
    isUpdatingURL.current = true; // URL 업데이트 시작
    
    const newParams = new URLSearchParams(searchParams);
    
    // 업데이트할 필터들만 적용 (다른 필터는 기존 URL 파라미터 유지)
    Object.entries(pendingURLUpdates).forEach(([key, value]) => {
      const paramKey = `${prefix}_${String(key)}`;
      
      if (value === undefined || value === '' || value === 'all') {
        // 기본값이면 URL에서 제거
        newParams.delete(paramKey);
      } else if (value === null) {
        // null은 'null' 문자열로 저장 (미입력 구분)
        newParams.set(paramKey, 'null');
      } else if (typeof value === 'boolean') {
        // boolean은 'true'/'false' 문자열로 저장
        newParams.set(paramKey, String(value));
      } else {
        newParams.set(paramKey, String(value));
      }
    });
    
    setSearchParams(newParams, { replace: true });
    setPendingURLUpdates({});
    
    // URL 업데이트 완료 후 플래그 해제
    // setSearchParams가 비동기이므로 다음 렌더링 사이클에서 해제
    setTimeout(() => {
      isUpdatingURL.current = false;
    }, 0);
  }, [pendingURLUpdates, prefix, searchParams, setSearchParams]);
  
  // 개별 필터 업데이트 함수 생성
  const createFilterSetter = useCallback(<K extends keyof T>(key: K) => {
    return (value: T[K]) => {
      setFilters(prev => {
        const updated = { ...prev, [key]: value };
        updateURL({ [key]: value } as unknown as Partial<T>);
        return updated;
      });
    };
  }, [updateURL]);
  
  // 필터 초기화
  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    const newParams = new URLSearchParams(searchParams);
    
    // 해당 prefix의 모든 파라미터 제거
    Object.keys(defaultFilters).forEach(key => {
      newParams.delete(`${prefix}_${key}`);
    });
    
    setSearchParams(newParams, { replace: true });
  }, [defaultFilters, prefix, searchParams, setSearchParams]);
  
  // URL이 변경되면 필터 상태 동기화 (브라우저 뒤로가기/앞으로가기 등)
  // 단, URL 업데이트 중이거나 pending 업데이트가 있으면 동기화하지 않음
  // searchParams.toString()을 사용하여 실제 변경 여부만 감지 (객체 참조 변경 방지)
  const searchParamsString = searchParams.toString();
  useEffect(() => {
    if (isUpdatingURL.current) return; // URL 업데이트 중이면 스킵
    if (Object.keys(pendingURLUpdates).length > 0) return; // pending 업데이트가 있으면 스킵
    
    const newFilters: Partial<T> = {};
    let hasChanges = false;
    
    for (const key in defaultFilters) {
      const urlValue = getInitialValue(key);
      // 필터 상태와 URL 값이 다를 때만 업데이트
      // 단, 현재 필터가 기본값이 아니고 URL에 값이 없으면 유지 (필터 변경으로 인한 URL 업데이트 중일 수 있음)
      if (urlValue !== filters[key]) {
        newFilters[key] = urlValue;
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      setFilters(prev => ({ ...prev, ...newFilters } as T));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString]); // searchParams.toString()으로 실제 변경만 감지
  
  // 간단한 API: 각 필터에 대해 [value, setValue] 튜플 반환
  const result: {
    resetFilters: () => void;
  } & {
    [K in keyof T]: [T[K], (value: T[K]) => void];
  } = {
    resetFilters,
  } as any;
  
  // 각 필터에 대해 [value, setValue] 튜플 추가
  for (const key in defaultFilters) {
    (result as any)[key] = [filters[key], createFilterSetter(key)];
  }
  
  return result;
};

